import { Dialog, DialogTitle, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import useLogoutMutation from '../../api/logout';
import Button, { OutlinedButton } from '../styledComponents/Button';
import { RemoveButton } from '../styledComponents/IconButtons';

const SESSION_TIMEOUT = 8 * 1000 * 60; // 8 minutes in ms
const PAYMENT_SESSION_TIMEOUT = 4 * 1000 * 60; // 4 minutes in ms

const eventTypes: (keyof WindowEventMap)[] = [
  'keypress',
  'mousemove',
  'mousedown',
  'scroll',
  'touchmove',
  'pointermove',
];

const addEventListeners = (listener: () => void) => {
  eventTypes.forEach((type) => {
    window.addEventListener(type, listener, false);
  });
};

const removeEventListeners = (listener: () => void) => {
  if (listener) {
    eventTypes.forEach((type) => {
      window.removeEventListener(type, listener, false);
    });
  }
};

const SessionTimeoutModal = () => {
  const logoutMutation = useLogoutMutation({
    isSessionTimeout: true,
  });

  const [isWarningModalOpen, setWarningModalOpen] = useState(false);
  // this manages the countdown in the modal, but the actual timer comes from createTimeout2() in the useEffect() below
  const [timeRemaining, setTimeRemaining] = useState(59);
  const [signingOut, setSigningOut] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  const userOnPaymentPage = () => {
    const paymentPagePaths = [
      '/payment',
      '/multiple-payment',
      '/autopay/method',
      '/wallet/edit',
      '/wallet/add',
    ];

    return !!paymentPagePaths.find((paymentPath) =>
      window.location.pathname.includes(paymentPath)
    );
  };

  useEffect(() => {
    if (isWarningModalOpen) {
      if (timeRemaining > 0) {
        setTimeout(() => setTimeRemaining((time) => time - 1), 1000);
      }
    }
  }, [timeRemaining, isWarningModalOpen]);

  const handleContinue = () => {
    setWarningModalOpen(false);
    setSigningOut(false);
    setTimeout(() => setTimeRemaining(59), 1000);

    const openChatButton = document.querySelector<HTMLButtonElement>(
      '[data-selector="GUIDE_CHANNEL_BUTTON"]'
    );

    if (openChatButton && chatOpen) {
      openChatButton?.click();
      setChatOpen(false);
    }
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await logoutMutation.mutateAsync();
    } catch (err) {
      // Error handled by mutation
    }
  };

  useEffect(() => {
    const createTimeout1 = () =>
      setTimeout(() => {
        setWarningModalOpen(true);
        const closeChatButton = document.querySelector<HTMLButtonElement>(
          '[data-selector="GUIDE_SINGLE_MENU_CLOSE_BUTTON"]'
        );

        // If the chat is open, close it and update state so we can re-open it when the user clicks "Yes, continue"
        if (closeChatButton) {
          setChatOpen(true);
          closeChatButton?.click();
        }
      }, SESSION_TIMEOUT);

    const createPaymentTimeout = () =>
      setTimeout(() => {
        setWarningModalOpen(true);
        const closeChatButton = document.querySelector<HTMLButtonElement>(
          '[data-selector="GUIDE_SINGLE_MENU_CLOSE_BUTTON"]'
        );

        // If the chat is open, close it and update state so we can re-open it when the user clicks "Yes, continue"
        if (closeChatButton) {
          setChatOpen(true);
          closeChatButton?.click();
        }
      }, PAYMENT_SESSION_TIMEOUT);

    const createTimeout2 = () =>
      setTimeout(() => {
        handleSignOut();
      }, 60000); // 1 minute timer that starts when the modal is opened

    const listener = () => {
      if (!isWarningModalOpen) {
        clearTimeout(timeout);
        timeout = userOnPaymentPage()
          ? createPaymentTimeout()
          : createTimeout1();
      }
    };

    const getTimeout = () => {
      if (isWarningModalOpen) {
        return createTimeout2();
      }

      return userOnPaymentPage() ? createPaymentTimeout() : createTimeout1();
    };

    let timeout = getTimeout();
    addEventListeners(listener);

    return () => {
      removeEventListeners(listener);
      clearTimeout(timeout);
    };
  }, [isWarningModalOpen]);

  return (
    <Dialog
      aria-labelledby="timeoutModalLabel"
      data-testid="session-timeout-dialog"
      sx={{ backdropFilter: 'blur(5px)', zIndex: 9999 }}
      open={isWarningModalOpen}
      onClose={handleContinue}
      PaperProps={{
        sx: {
          borderRadius: 4,
          margin: 0,
          px: { mobile: 3, tablet: 4 },
          position: { mobile: 'fixed', tablet: 'relative' },
          width: { mobile: 1, tablet: 480 },
          height: 'auto',
          maxHeight: 1,
        },
      }}
    >
      <Stack direction="column" alignItems="center" sx={{ width: 1 }}>
        <Stack
          direction="row"
          justifyContent="flex-end"
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: 1,
            mt: { mobile: 2, tablet: 3 },
          }}
        >
          <RemoveButton
            aria-label="Close timeout dialog"
            data-testid="session-timeout-dialog-close-button"
            onClick={handleContinue}
            sx={{ p: 0 }}
            autoFocus
          />
        </Stack>
        <DialogTitle
          id="timeoutModalLabel"
          sx={{
            fontSize: { mobile: 23, tablet: 28 },
            fontWeight: { mobile: 700, tablet: 500 },
            textAlign: 'center',
            p: 0,
            lineHeight: { mobile: '32px', tablet: '40px' },
            mt: { mobile: 0, tablet: 3 },
          }}
        >
          Are you still there?
        </DialogTitle>
        <Typography
          sx={{
            fontSize: { mobile: 39, tablet: 54 },
            fontWeight: 400,
            textAlign: 'center',
            lineHeight: { mobile: '48px', tablet: '72px' },
            mt: { mobile: 2, tablet: 3 },
          }}
        >
          00:{timeRemaining < 10 ? `0${timeRemaining}` : timeRemaining}
        </Typography>
        <Typography
          sx={{
            fontSize: { mobile: 16, tablet: 18 },
            fontWeight: 400,
            lineHeight: '24px',
            mt: 2,
          }}
        >
          Your session is about to end due to inactivity. Do you want to
          continue?
        </Typography>
        <OutlinedButton
          data-testid="session-timeout-dialog-sign-out-button"
          loading={signingOut}
          width={1}
          sx={{ mt: { mobile: 3, tablet: 5 } }}
          onClick={handleSignOut}
        >
          Sign out
        </OutlinedButton>
        <Button
          data-testid="session-timeout-dialog-continue-button"
          disabled={signingOut}
          fullWidth
          sx={{ mt: { mobile: 1, tablet: 2 }, mb: 5 }}
          onClick={handleContinue}
        >
          Yes, continue
        </Button>
      </Stack>
    </Dialog>
  );
};

export default SessionTimeoutModal;
